import React, { useState, useEffect, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getAllUsers, getUserByQuery } from '../../actions/user';
import { 
    Users as UsersIcon, 
    Plus, 
    Search, 
    Phone, 
    Mail, 
    Shield, 
    CheckCircle,
    XCircle,
    User,
    Filter,
    RefreshCw,
    Eye,
    MapPin
} from 'lucide-react';


const Users = ({getAllUsers, getUserByQuery, users, user, pages, activePage, loading }) => {

    const [data, setData] = useState([])
    const [pageCount, setPage] = useState(0);
    const [currentPage, setActivePage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [roleFilter, setRoleFilter] = useState('all');
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    useEffect( () => {
        if(user !== undefined && user !== null && users === null){
            let userData = [{
                role: user.role,
                _id: user._id,
                phone: user.phone,
                name: user.name,
                email: user.email,
                suspend: user.suspend,
                hasAddress: user.hasAddress,
                createdAt: user.createdAt
            }];
            setData(userData);
            setPage(1);
            setActivePage(1);
            return;
        }
        if(users === undefined || users.length === 0){
            getAllUsers(1, limit); // Pass page and limit
        }else{
            let usersData = []
            users.forEach(user => {
                usersData.push({
                    role: user.role,
                    _id: user._id,
                    phone: user.phone,
                    name: user.name,
                    email: user.email,
                    suspend: user.suspend,
                    hasAddress: user.hasAddress,
                    createdAt: user.createdAt
                });
            });
            setData(usersData);
            setPage(pages);
            setActivePage(activePage);
        }
    }, [ users, user ] );

    // Apply client-side filtering based on search term and role filter
    useEffect(() => {
        let filtered = [...data];

        // Apply search term filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(user => 
                (user.name && user.name.toLowerCase().includes(searchLower)) ||
                (user.email && user.email.toLowerCase().includes(searchLower)) ||
                (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
                (user.role && user.role.toLowerCase().includes(searchLower)) ||
                (user._id && user._id.toLowerCase().includes(searchLower))
            );
        }

        // Apply role filter
        if (roleFilter !== 'all') {
            filtered = filtered.filter(user => 
                user.role && user.role.toLowerCase() === roleFilter.toLowerCase()
            );
        }

        setFilteredData(filtered);
    }, [data, searchTerm, roleFilter]);

    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [limit] = useState(10);

    const initialState = {
        email:'',
        phone:''
    };

    const [formData, setFormData] = useState(initialState);

    const {
        email,
        phone
    } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const onSubmit = e => {
        e.preventDefault();
        getUserByQuery(formData.phone, formData.email);        
    };

    // Calculate statistics based on all data (not filtered)
    const statistics = useMemo(() => {
        if (!data.length) return null;

        const totalUsers = data.length;
        const activeUsers = data.filter(user => !user.suspend).length;
        const suspendedUsers = data.filter(user => user.suspend).length;
        
        const roles = data.reduce((acc, user) => {
            acc[user.role] = (acc[user.role] || 0) + 1;
            return acc;
        }, {});

        return {
            totalUsers,
            activeUsers,
            suspendedUsers,
            roles,
            uniqueRoles: Object.keys(roles)
        };
    }, [data]);

    // Calculate filtered statistics for display context
    const filteredStatistics = useMemo(() => {
        if (!filteredData.length) return null;

        const totalFiltered = filteredData.length;
        const activeFiltered = filteredData.filter(user => !user.suspend).length;
        const suspendedFiltered = filteredData.filter(user => user.suspend).length;

        return {
            totalFiltered,
            activeFiltered,
            suspendedFiltered
        };
    }, [filteredData]);

    const getRoleColor = (role) => {
        switch (role?.toLowerCase()) {
            case 'admin': return 'danger';
            case 'manager': return 'warning';
            case 'executive': return 'info';
            case 'user': return 'primary';
            case 'customer': return 'success';
            default: return 'secondary';
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortedData = () => {
        if (!sortField) return filteredData;
        
        return [...filteredData].sort((a, b) => {
            const aValue = a[sortField] || '';
            const bValue = b[sortField] || '';
            
            if (sortDirection === 'asc') {
                return aValue.toString().localeCompare(bValue.toString());
            } else {
                return bValue.toString().localeCompare(aValue.toString());
            }
        });
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    // Server-side pagination logic
    const handlePageChange = (page) => {
        getAllUsers(page, limit);
    };

    const handlePageInput = (event) => {
        const page = event.target.value ? Number(event.target.value) : 1;
        if (page >= 1 && page <= pages) {
            getAllUsers(page, limit);
        }
    };

    const handleRefresh = () => {
        getAllUsers(activePage || 1, limit);
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <UsersIcon size={32} className="me-3 text-primary" />
                                    User Management
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage user accounts, roles, and permissions
                                </p>
                            </div>
                            <div className="d-flex gap-2">
                                <button 
                                    onClick={handleRefresh}
                                    className="btn btn-outline-primary btn-lg shadow-sm d-flex align-items-center"
                                    disabled={loading}
                                >
                                    <RefreshCw size={20} className="me-2" />
                                    {loading ? 'Refreshing...' : 'Refresh'}
                                </button>
                                <Link 
                                    to="/inventory/v1.2/users/new"
                                    className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                                >
                                    <Plus size={20} className="me-2" />
                                    Add New User
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Cards */}
                {statistics && (
                    <div className="row mb-4">
                        <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-primary text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.totalUsers}</div>
                                            <div className="small">Total Users</div>
                                        </div>
                                        <UsersIcon size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-success text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.activeUsers}</div>
                                            <div className="small">Active Users</div>
                                        </div>
                                        <CheckCircle size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-warning text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.suspendedUsers}</div>
                                            <div className="small">Suspended</div>
                                        </div>
                                        <XCircle size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                            <div className="card shadow-sm border-0 bg-info text-white">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="h4 mb-0">{statistics.uniqueRoles.length}</div>
                                            <div className="small">User Roles</div>
                                        </div>
                                        <User size={28} className="opacity-75" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Search and Filter Section */}
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search users by name, email, phone, or role..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setSearchTerm('')}
                                            title="Clear search"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <select
                                    className="form-select"
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                >
                                    <option value="all">All Roles</option>
                                    {statistics?.uniqueRoles.map(role => (
                                        <option key={role} value={role}>
                                            {role.charAt(0).toUpperCase() + role.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Active Filters Indicator */}
                {(searchTerm || roleFilter !== 'all') && filteredStatistics && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="alert alert-info d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>Filters Active:</strong> Showing {filteredStatistics.totalFiltered} of {statistics.totalUsers} users
                                    {searchTerm && <span className="ms-2 badge bg-primary">Search: "{searchTerm}"</span>}
                                    {roleFilter !== 'all' && <span className="ms-2 badge bg-info">Role: {roleFilter}</span>}
                                </div>
                                <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setRoleFilter('all');
                                    }}
                                >
                                    Clear All Filters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Advanced Search Toggle */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-white border-bottom">
                                <button
                                    className="btn btn-link text-decoration-none p-0 d-flex align-items-center"
                                    onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                                >
                                    <Filter size={16} className="me-2" />
                                    Advanced Search
                                    <span className="ms-2">
                                        {showAdvancedSearch ? '▼' : '▶'}
                                    </span>
                                </button>
                            </div>
                            {showAdvancedSearch && (
                                <div className="card-body">
                                    <form onSubmit={onSubmit}>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label d-flex align-items-center">
                                                    <Phone size={16} className="me-2 text-muted" />
                                                    Phone Number
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter phone number"
                                                    name="phone"
                                                    value={phone}
                                                    onChange={onChange}
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label d-flex align-items-center">
                                                    <Mail size={16} className="me-2 text-muted" />
                                                    Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    placeholder="Enter email address"
                                                    name="email"
                                                    value={email}
                                                    onChange={onChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button type="submit" className="btn btn-primary">
                                                <Search size={16} className="me-2" />
                                                Search Users
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-outline-secondary"
                                                onClick={() => setFormData(initialState)}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="card shadow-sm">
                    <div className="card-header bg-white border-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0 d-flex align-items-center">
                                <UsersIcon size={20} className="me-2 text-primary" />
                                Users Directory
                            </h5>
                            <div className="d-flex align-items-center text-muted">
                                <span className="small">
                                    Page {activePage || 1} of {pages || 1} • Showing {filteredData.length} of {data.length} users
                                    {searchTerm && ` matching "${searchTerm}"`}
                                    {roleFilter !== 'all' && ` with role "${roleFilter}"`}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading users...</p>
                            </div>
                        ) : data.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="mb-4">
                                    <UsersIcon size={64} className="text-muted" />
                                </div>
                                <h4 className="text-muted mb-3">No users found</h4>
                                <p className="text-muted mb-4">
                                    No users have been created yet. Get started by adding your first user.
                                </p>
                                <Link 
                                    to="/inventory/v1.2/users/new"
                                    className="btn btn-primary btn-lg"
                                >
                                    <Plus size={20} className="me-2" />
                                    Add Your First User
                                </Link>
                            </div>
                        ) : filteredData.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="mb-4">
                                    <Search size={64} className="text-muted" />
                                </div>
                                <h4 className="text-muted mb-3">No matching users found</h4>
                                <p className="text-muted mb-4">
                                    No users match your search criteria. Try adjusting your filters.
                                </p>
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setRoleFilter('all');
                                    }}
                                >
                                    Clear Filters
                                </button>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th 
                                                scope="col" 
                                                className="cursor-pointer user-select-none"
                                                onClick={() => handleSort('name')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <User size={16} className="me-2 text-muted" />
                                                    Name {getSortIcon('name')}
                                                </div>
                                            </th>
                                            <th 
                                                scope="col" 
                                                className="cursor-pointer user-select-none"
                                                onClick={() => handleSort('phone')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <Phone size={16} className="me-2 text-muted" />
                                                    Phone {getSortIcon('phone')}
                                                </div>
                                            </th>
                                            <th 
                                                scope="col" 
                                                className="cursor-pointer user-select-none"
                                                onClick={() => handleSort('email')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <Mail size={16} className="me-2 text-muted" />
                                                    Email {getSortIcon('email')}
                                                </div>
                                            </th>
                                            <th 
                                                scope="col" 
                                                className="cursor-pointer user-select-none"
                                                onClick={() => handleSort('role')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <Shield size={16} className="me-2 text-muted" />
                                                    Role {getSortIcon('role')}
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <CheckCircle size={16} className="me-2 text-muted" />
                                                    Status
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <MapPin size={16} className="me-2 text-muted" />
                                                    Address
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getSortedData().map((user, index) => (
                                            <tr key={`user-${index}`} className="align-middle">
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                                            <User size={16} className="text-primary" />
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold text-dark">
                                                                {user.name || 'N/A'}
                                                            </div>
                                                            <div className="small text-muted">
                                                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() + ' ' + new Date(user.createdAt).toLocaleTimeString() : 'N/A'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Phone size={14} className="me-2 text-muted" />
                                                        <span>{user.phone || 'N/A'}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Mail size={14} className="me-2 text-muted" />
                                                        <span className="text-break">{user.email || 'N/A'}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${getRoleColor(user.role)} bg-opacity-10 text-${getRoleColor(user.role)} border border-${getRoleColor(user.role)} border-opacity-25`}>
                                                        {user.role || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${user.suspend ? 'bg-danger' : 'bg-success'} bg-opacity-10 text-${user.suspend ? 'danger' : 'success'} border border-${user.suspend ? 'danger' : 'success'} border-opacity-25`}>
                                                        {user.suspend ? 'Suspended' : 'Active'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${user.hasAddress ? 'bg-success' : 'bg-warning'} bg-opacity-10 text-${user.hasAddress ? 'success' : 'warning'} border border-${user.hasAddress ? 'success' : 'warning'} border-opacity-25`}>
                                                        {user.hasAddress ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <Link 
                                                        to={`/inventory/v1.2/users/${user._id}`}
                                                        className="btn btn-sm btn-outline-primary"
                                                        title="View User Details"
                                                    >
                                                        <Eye size={14} className="me-1" />
                                                        View
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                
                                {/* Server-side Pagination */}
                                {pages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                        <div className="d-flex align-items-center">
                                            <span className="small text-muted me-3">
                                                Page {activePage || 1} of {pages || 1}
                                            </span>
                                            <div className="d-flex align-items-center">
                                                <span className="small text-muted me-2">Go to:</span>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={pages}
                                                    defaultValue={activePage || 1}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handlePageInput(e);
                                                        }
                                                    }}
                                                    onBlur={handlePageInput}
                                                    className="form-control form-control-sm"
                                                    style={{ width: '70px' }}
                                                />
                                            </div>
                                        </div>
                                        
                                        <nav aria-label="User pagination">
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${(activePage || 1) === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(1)}
                                                        disabled={(activePage || 1) === 1}
                                                        title="First page"
                                                    >
                                                        ««
                                                    </button>
                                                </li>
                                                <li className={`page-item ${(activePage || 1) === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange((activePage || 1) - 1)}
                                                        disabled={(activePage || 1) === 1}
                                                        title="Previous page"
                                                    >
                                                        ‹
                                                    </button>
                                                </li>
                                                
                                                {/* Page numbers */}
                                                {(() => {
                                                    const pageNumbers = [];
                                                    const currentPage = activePage || 1;
                                                    const totalPages = pages || 1;
                                                    const startPage = Math.max(1, currentPage - 2);
                                                    const endPage = Math.min(totalPages, currentPage + 2);
                                                    
                                                    for (let i = startPage; i <= endPage; i++) {
                                                        pageNumbers.push(
                                                            <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(i)}
                                                                >
                                                                    {i}
                                                                </button>
                                                            </li>
                                                        );
                                                    }
                                                    return pageNumbers;
                                                })()}
                                                
                                                <li className={`page-item ${(activePage || 1) === (pages || 1) ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange((activePage || 1) + 1)}
                                                        disabled={(activePage || 1) === (pages || 1)}
                                                        title="Next page"
                                                    >
                                                        ›
                                                    </button>
                                                </li>
                                                <li className={`page-item ${(activePage || 1) === (pages || 1) ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pages || 1)}
                                                        disabled={(activePage || 1) === (pages || 1)}
                                                        title="Last page"
                                                    >
                                                        »»
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Role Distribution Cards */}
                {statistics && statistics.uniqueRoles.length > 0 && (
                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header bg-white border-bottom">
                                    <h5 className="card-title mb-0 d-flex align-items-center">
                                        <User size={20} className="me-2 text-primary" />
                                        Role Distribution
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        {statistics.uniqueRoles.map(role => (
                                            <div key={role} className="col-lg-3 col-md-6 mb-3">
                                                <div className={`card border-${getRoleColor(role)} h-100`}>
                                                    <div className="card-body text-center">
                                                        <div className={`h4 mb-2 text-${getRoleColor(role)}`}>
                                                            {statistics.roles[role]}
                                                        </div>
                                                        <div className="small text-muted text-uppercase fw-bold">
                                                            {role}
                                                        </div>
                                                        <div className="mt-2">
                                                            <span className={`badge bg-${getRoleColor(role)} bg-opacity-10 text-${getRoleColor(role)}`}>
                                                                {((statistics.roles[role] / statistics.totalUsers) * 100).toFixed(1)}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    )
}

Users.propTypes = {
    getAllUsers: PropTypes.func.isRequired,
    getUserByQuery: PropTypes.func.isRequired,
    pages: PropTypes.number,
    activePage: PropTypes.number,
    loading: PropTypes.bool
} 

const mapStateToProps = state => ({
    users: state.user.users,
    user: state.user.user,
    pages: state.user.pages,
    activePage : state.user.activePage,
    loading: state.user.loading
})

export default connect( mapStateToProps, { getAllUsers, getUserByQuery } )( Users );